// The facts a Saju book prints for one person, from the chart the Saju app returns for them (birthInfo, saju,
// seun_list_all, daeun_list): each of the 100 years' Engine A score, its four terms and the sentences that explain it.
// One implementation for the book builder (evidence.json through prepare-product-evidence.mjs), the Saju app
// (seun-series-facts.ts, which adds the reviewed sample's signature check) and the Studio, which writes a book's text
// for another person (specs/011).
// Source: web/services/fortune_calculator.py — calculate_yearly_fortune, _luck_axis_scores, _luck_stem_bonus,
// calc_zodiac_score and calc_day_score. Not the live frontend yearly helper: it omits the stem term.

/** A chart that is not what the Saju app returns, or whose score the formula does not reproduce. */
export class SajuChartError extends Error {
  constructor(detail) {
    super(`명식 원본 검증 실패: ${detail}`);
    this.name = 'SajuChartError';
    this.detail = detail;
  }
}
const fail = detail => { throw new SajuChartError(detail); };

export const SEUN_SCORE_FORMULA_NOTE = '참고 지수는 구형 Engine A의 합산값입니다. 천간 항의 간지 순번 보정은 명리 해석이 아닌 구현 규칙입니다. 대운·월지·시지·신강약은 이 산식에 반영되지 않으며, 원문에 표시된 모든 합충·신살이 점수 항인 것도 아닙니다. 항별 차이는 환산 전 값이고 최종 점수 차이와 단위가 다릅니다.';

const STEMS = ['갑', '을', '병', '정', '무', '기', '경', '신', '임', '계'];
const BRANCHES = ['자', '축', '인', '묘', '진', '사', '오', '미', '신', '유', '술', '해'];
const BRANCH_ELEMENTS = [4, 2, 0, 0, 2, 1, 1, 2, 3, 3, 2, 4];
const STEM_HANJA = [...'甲乙丙丁戊己庚辛壬癸'];
const BRANCH_HANJA = [...'子丑寅卯辰巳午未申酉戌亥'];
const TEN_GOD_BONUS = Object.freeze({
  정재: 10, 정관: 8, 정인: 8, 식신: 6, 편재: 6,
  비견: 2, 겁재: -4, 상관: -4, 편인: -4, 편관: -6,
});
const PAIRS = Object.freeze({
  he: ['자축', '인해', '묘술', '진유', '사신', '오미'],
  chong: ['자오', '축미', '인신', '묘유', '진술', '사해'],
  po: ['자유', '축진', '인해', '묘오', '미술', '사신'],
  hae: ['자미', '축오', '인사', '묘진', '신해', '유술'],
});

function object(value, label) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) fail(`${label} 객체가 없습니다.`);
  return value;
}
function string(value, label) {
  if (typeof value !== 'string' || !value.trim()) fail(`${label} 문자열이 없습니다.`);
  return value;
}
function integer(value, label, min = 0, max = 9999) {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < min || value > max) fail(`${label} 정수 범위 오류입니다.`);
  return value;
}
function array(value, label, length) {
  if (!Array.isArray(value) || value.length !== length) fail(`${label}은 ${length}개여야 합니다.`);
  return value;
}
function relations(value, label) {
  const source = object(value, label);
  const read = key => {
    if (!Array.isArray(source[key])) fail(`${label}.${key} 배열이 없습니다.`);
    return source[key].map((item, i) => string(item, `${label}.${key}[${i}]`));
  };
  return { clash: read('clash'), harmony: read('harmony') };
}
function ganjiIndex(stem, branch) {
  const index = Array.from({ length: 60 }, (_, i) => i).find(i => STEMS[i % 10] === stem && BRANCHES[i % 12] === branch);
  if (index === undefined) fail(`유효하지 않은 간지 ${stem}${branch}`);
  return index;
}
function tenGod(dayMaster, stem) {
  const a = STEMS.indexOf(dayMaster), b = STEMS.indexOf(stem);
  if (a < 0 || b < 0) fail('십성의 천간이 유효하지 않습니다.');
  const dm = Math.floor(a / 2), target = Math.floor(b / 2), same = a % 2 === b % 2;
  if (dm === target) return same ? '비견' : '겁재';
  if ((dm + 1) % 5 === target) return same ? '식신' : '상관';
  if ((target + 1) % 5 === dm) return same ? '편인' : '정인';
  if ((dm + 2) % 5 === target) return same ? '편재' : '정재';
  return same ? '편관' : '정관';
}
function readLuck(value, label, dayMaster) {
  const row = object(value, label);
  const stem = string(row.stem, `${label}.stem`), branch = string(row.branch, `${label}.branch`);
  ganjiIndex(stem, branch);
  const ganji = string(row.ganji, `${label}.ganji`);
  if (ganji !== stem + branch) fail(`${label} 간지와 천간·지지가 다릅니다.`);
  const stemGod = string(row.ten_god_stem, `${label}.ten_god_stem`);
  const branchGod = string(row.ten_god_branch, `${label}.ten_god_branch`);
  if (stemGod !== tenGod(dayMaster, stem) || !(branchGod in TEN_GOD_BONUS)) fail(`${label} 십성이 유효하지 않습니다.`);
  return {
    ganji, stem, branch, ten_god_stem: stemGod, ten_god_branch: branchGod,
    balance: integer(object(row.scores, `${label}.scores`).balance, `${label}.scores.balance`, 0, 100),
    stemRelations: relations(row.stem_relations, `${label}.stem_relations`),
    branchRelations: relations(row.branch_relations, `${label}.branch_relations`),
  };
}

/**
 * One person's chart as the Saju app returns it, read into what the facts are made of: the birth, the natal pillars
 * (three when the hour of birth is not known), the 100 years from the first 대운 on and the ten 대운. Checks what holds for
 * anyone — consecutive years whose age and pillar are that year's, ten consecutive ten-year 대운 — not which years or ages
 * they are.
 */
export function readSajuChart(payload) {
  const root = object(payload, 'payload'), birthInfo = object(root.birthInfo, 'birthInfo');
  const solar = string(birthInfo.solar, 'birthInfo.solar');
  if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(solar)) fail('양력 출생시각 형식이 다릅니다.');
  const birthYear = Number(solar.slice(0, 4));
  const saju = object(root.saju, 'saju');
  if (typeof birthInfo.unknownTime !== 'boolean') fail('birthInfo.unknownTime 가 참·거짓이 아닙니다.');
  // A birth whose hour is not known has no hour pillar: the engine gives none, and nothing here reads one.
  const natal = ['year', 'month', 'day', 'hour'].filter(label => label !== 'hour' || !birthInfo.unknownTime).map(label => {
    const pillar = object(saju[label], `saju.${label}`), ganji = object(pillar.ganji, `saju.${label}.ganji`);
    const stem = string(ganji.stem, `${label}.stem`), branch = string(ganji.branch, `${label}.branch`);
    ganjiIndex(stem, branch);
    if (ganji.stemHanja !== STEM_HANJA[STEMS.indexOf(stem)] || ganji.branchHanja !== BRANCH_HANJA[BRANCHES.indexOf(branch)]) fail(`${label} 원국 한자가 일치하지 않습니다.`);
    return { label, stem, branch, stemTenGod: string(pillar.stemTenGod, `${label}.stemTenGod`), branchTenGod: string(pillar.branchTenGod, `${label}.branchTenGod`) };
  });
  const dayMaster = natal[2].stem;
  let firstYear = null;
  const annual = array(root.seun_list_all, 'seun_list_all', 100).map((value, i) => {
    const row = object(value, `seun_list_all[${i}]`), luck = readLuck(row, `seun_list_all[${i}]`, dayMaster);
    const year = integer(row.year, `연도[${i}]`), age = integer(row.age, `나이[${i}]`);
    firstYear ??= year;
    if (year !== firstYear + i || age !== year - birthYear + 1) fail(`${i + 1}행 연도·나이가 이어지지 않습니다.`);
    if (ganjiIndex(luck.stem, luck.branch) !== ((year - 1984) % 60 + 60) % 60) fail(`${year}년의 간지가 일치하지 않습니다.`);
    return { year, age, ...luck };
  });
  const daeun = array(root.daeun_list, 'daeun_list', 10).map((value, i) => {
    const row = object(value, `daeun_list[${i}]`), luck = readLuck(row, `daeun_list[${i}]`, dayMaster);
    const index = integer(row.index, `대운 번호[${i}]`, 1, 10);
    const start_age = integer(row.start_age, `대운 시작[${i}]`), end_age = integer(row.end_age, `대운 끝[${i}]`);
    return { index, start_age, end_age, ...luck };
  });
  daeun.forEach((row, i) => {
    if (row.index !== i + 1 || row.end_age !== row.start_age + 9 || (i > 0 && row.start_age !== daeun[i - 1].start_age + 10)) fail(`${i + 1}대운의 번호·나이 구간이 일치하지 않습니다.`);
  });
  const birth = {};
  for (const key of ['solar', 'lunar', 'age', 'unknownTime', 'clockTime', 'trueSolarTime', 'dstInEffect', 'dstCorrection']) {
    if (!(key in birthInfo)) fail(`birthInfo.${key}가 없습니다.`);
    birth[key] = birthInfo[key];
  }
  birth.correction = object(birthInfo.correction, 'birthInfo.correction');
  return { birth, natal, bodyStrength: string(saju.bodyStrength, 'saju.bodyStrength'),
    ohaengDist: object(saju.ohaengDist, 'saju.ohaengDist'), tenGods: object(saju.tenGods, 'saju.tenGods'), annual, daeun };
}

function elementScore(reference, target) {
  const a = BRANCH_ELEMENTS[BRANCHES.indexOf(reference)], b = BRANCH_ELEMENTS[BRANCHES.indexOf(target)];
  if ((b + 1) % 5 === a) return 15;
  if (a === b) return 5;
  if ((a + 1) % 5 === b) return -5;
  if ((a + 2) % 5 === b) return 10;
  if ((b + 2) % 5 === a) return -15;
  return fail('지지 오행 관계를 계산할 수 없습니다.');
}
function branchScore(target, reference, day) {
  const has = kind => PAIRS[kind].some(pair => pair === target + reference || pair === reference + target) ? 1 : 0;
  const raw = (day ? 20 : 15) * has('he') - 20 * has('chong') - 10 * has('po') - (day ? 8 : 5) * has('hae');
  // Engine A deliberately substitutes the element term whenever the relation sum is zero.
  const value = raw === 0 ? elementScore(reference, target) : raw;
  const limit = day ? 30 : 20;
  return Math.trunc(Math.max(-limit, Math.min(limit, value)));
}
const signed = value => `${value >= 0 ? '+' : '−'}${Math.abs(value)}`;

/**
 * The 100 years of a chart read by readSajuChart: each year's pillar, ten gods, Engine A score and its four raw terms
 * (with their change from the year before), its 대운, relations, and the foundation and calculation sentences.
 */
export function seunSeriesFacts(chart) {
  const yearBranch = chart.natal[0].branch, dayBranch = chart.natal[2].branch;
  const rows = [];
  for (const annual of chart.annual) {
    const daeun = chart.daeun.find(row => annual.age >= row.start_age && annual.age <= row.end_age);
    if (!daeun) fail(`${annual.year}년을 포함하는 대운이 없습니다.`);
    const jitter = ganjiIndex(annual.stem, annual.branch) % 7 - 3;
    const stemBonus = TEN_GOD_BONUS[annual.ten_god_stem];
    const values = [branchScore(annual.branch, yearBranch, false), branchScore(annual.branch, dayBranch, true), elementScore(yearBranch, annual.branch), (stemBonus + jitter) * 1.5];
    const sum = values.reduce((total, value) => total + value, 0);
    const raw = Math.trunc(Math.max(-95, Math.min(95, sum)));
    const balance = Math.round((raw + 95) / 190 * 100);
    if (balance !== annual.balance) fail(`${annual.year}년 balance ${annual.balance}가 구형 Engine A 재계산 ${balance}와 다릅니다.`);
    const previous = rows.at(-1);
    const parts = ['년지 항', '일지 항', '오행 항', '천간 항'].map((label, i) => ({ label, value: values[i], ...(previous ? { delta: values[i] - previous.parts[i].value } : {}) }));
    rows.push({
      year: annual.year, age: annual.age, ganji: annual.ganji, stem: annual.stem, branch: annual.branch,
      stemTenGod: annual.ten_god_stem, branchTenGod: annual.ten_god_branch, balance,
      ...(previous ? { previousBalance: previous.balance, delta: balance - previous.balance } : {}),
      daeunIndex: daeun.index, daeunGanji: daeun.ganji, daeunChanged: previous !== undefined && previous.daeunIndex !== daeun.index,
      relations: [
        ...annual.stemRelations.clash.map(item => `천간 충: ${item}`), ...annual.stemRelations.harmony.map(item => `천간 합: ${item}`),
        ...annual.branchRelations.clash.map(item => `지지 긴장 관계: ${item}`), ...annual.branchRelations.harmony.map(item => `지지 결합 관계: ${item}`),
      ],
      foundation: `${chart.natal[2].stem} 일간 · 원국 년지 ${yearBranch} / 일지 ${dayBranch} · ${daeun.index}대운 ${daeun.ganji}(${daeun.start_age}–${daeun.end_age}세). 세운 천간 ${annual.stem}(${annual.ten_god_stem}) · 지지 ${annual.branch}(${annual.ten_god_branch}).`,
      calculation: `구형 Engine A: ${parts.map(part => `${part.label} ${signed(part.value)}`).join(' · ')} = ${signed(sum)} → 소수 버림 ${signed(raw)} → 0–100 환산 ${balance}점. 천간 항은 (${annual.ten_god_stem} ${signed(stemBonus)} + 순번 보정 ${signed(jitter)}) × 1.5이며, 보정값은 명리 근거가 아닌 구현 규칙입니다.`,
      parts,
    });
  }
  return rows;
}

/**
 * The twelve 절기 months of /api/wolun_list as the Saju app shows them and a book prints them: numbered by their solar
 * month (the engine's own number kept as engine_month), with the pillar, the ten gods and the period in the names the
 * app gives them.
 */
export function readSajuMonths(wolunList) {
  return array(wolunList, 'wolun_list', 12).map((value, i) => {
    const row = object(value, `wolun_list[${i}]`);
    const stem = string(row.stem, `wolun_list[${i}].stem`), branch = string(row.branch, `wolun_list[${i}].branch`);
    ganjiIndex(stem, branch);
    return { ...row, month: integer(row.solar_month, `wolun_list[${i}].solar_month`, 1, 12), engine_month: integer(row.month, `wolun_list[${i}].month`, 1, 12),
      period: string(row.period_label, `wolun_list[${i}].period_label`), ganji: stem + branch,
      stem_tg: string(row.ten_god_stem, `wolun_list[${i}].ten_god_stem`), branch_tg: string(row.ten_god_branch, `wolun_list[${i}].ten_god_branch`) };
  });
}

/**
 * Everything a Saju book is built from, read once from the chart the Saju app returns for one person (the local
 * engine's `chart`, the builder's chart-payload.json): the chart, its hundred years' facts, its twelve months, and the
 * engine's own reading of each 대운 and year (their `fortune`), which the manuscripts were written beside.
 */
export function readBookChart(payload) {
  const chart = readSajuChart(payload);
  return { chart, years: seunSeriesFacts(chart), months: readSajuMonths(object(payload, 'payload').wolun_list),
    readings: { daeun: payload.daeun_list.map(row => row.fortune ?? null), seun: payload.seun_list_all.map(row => row.fortune ?? null) } };
}

/**
 * What a manuscript of `scope` was written from: the natal chart and, for one 대운 ({ series: 'daeun', period }) or one
 * year ({ series: 'seun', year }), that period's facts and the engine's reading of it; a product's own text
 * ({ series: 'product', product }) rests on the natal chart. A period the person's book does not have is refused.
 */
export function scopeFacts(book, scope) {
  const natal = { pillars: book.chart.natal, bodyStrength: book.chart.bodyStrength, ohaengDist: book.chart.ohaengDist, tenGods: book.chart.tenGods };
  if (scope?.series === 'product' && typeof scope.product === 'string') return { scope: { series: 'product', product: scope.product }, natal };
  if (scope?.series === 'daeun') {
    const index = book.chart.daeun.findIndex(row => row.index === scope.period);
    if (index < 0) fail(`${scope.period}대운이 이 명식에 없습니다.`);
    return { scope: { series: 'daeun', period: scope.period }, natal, daeun: book.chart.daeun[index], reading: book.readings.daeun[index] };
  }
  if (scope?.series === 'seun') {
    const index = book.years.findIndex(row => row.year === scope.year);
    if (index < 0) fail(`${scope.year}년이 이 명식의 100년 안에 없습니다.`);
    return { scope: { series: 'seun', year: scope.year }, natal, year: book.years[index], reading: book.readings.seun[index] };
  }
  return fail('원고 범위는 대운 한 편, 세운 한 해 또는 상품 글입니다.');
}

const canonical = value => Array.isArray(value) ? `[${value.map(canonical).join(',')}]`
  : value !== null && typeof value === 'object' ? `{${Object.keys(value).sort().filter(key => value[key] !== undefined).map(key => `${JSON.stringify(key)}:${canonical(value[key])}`).join(',')}}`
    : JSON.stringify(value);

/** The SHA-256 of facts in a canonical JSON (keys sorted): a manuscript written from other facts is to be written again. */
export async function factsHash(facts) {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical(facts)));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}
